import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { useCan } from '../../lib/session.js';
import { DerivedFigure, StatusChip } from '../../components/qaqc/DerivedFigure.js';
import {
  ApiError,
  approveDocumentGovernance,
  fetchDocumentGovernance,
  fetchFortnightRollUp,
  saveDocumentGovernance,
  sendBackDocumentGovernance,
  submitDocumentGovernance,
} from '../../lib/api.js';
import { ProjectPicker, useProjects } from '../../components/qaqc/ProjectPicker.js';
import { FormPromptPanel } from '../../components/ai/FormPromptPanel.js';
import { disposeFormDraft, requestLogEntryDraft } from '../../lib/form-prompt-api.js';
import { t } from '../../lib/i18n.js';

/** The counts a sentence can fill in (pass 8). Derived figures never come from one. */
const PROMPT_COUNTS = [
  'submittalsTotal',
  'drawingsTotal',
  'statusApproved',
  'statusResubmit',
  'statusRejected',
  'statusUnderReview',
  'reviewDaysTotal',
  'reviewedCount',
  'correspondenceIncoming',
  'correspondenceOutgoing',
] as const;

/**
 * The Daily Document Governance Log (spec §9).
 *
 * Its own cycle, not part of the monthly package: entered and approved daily,
 * distributed fortnightly. The roll-up at the bottom is that distribution view —
 * it aggregates a window rather than approving one.
 */

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The fortnight ending today, as the default roll-up window. */
function defaultFortnight(): { from: string; to: string } {
  const end = new Date();
  const start = new Date(end.getTime() - 13 * 86_400_000);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

const COUNT_FIELDS = [
  { key: 'submittalsTotal', label: 'Submittals' },
  { key: 'drawingsTotal', label: 'Drawings' },
  { key: 'statusApproved', label: 'Approved' },
  { key: 'statusResubmit', label: 'Resubmit' },
  { key: 'statusRejected', label: 'Rejected' },
  { key: 'statusUnderReview', label: 'Under review' },
  { key: 'reviewDaysTotal', label: 'Review days (total)' },
  { key: 'reviewedCount', label: 'Reviews completed' },
  { key: 'correspondenceIncoming', label: 'Correspondence in' },
  { key: 'correspondenceOutgoing', label: 'Correspondence out' },
] as const;

export function DocumentGovernance() {
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useState('');
  const { label } = useProjects();
  /** Remarks typed for a send-back, per entry. */
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [window, setWindow] = useState(defaultFortnight());

  const [draft, setDraft] = useState({
    logDate: today(),
    submittalsTotal: 0,
    drawingsTotal: 0,
    statusApproved: 0,
    statusResubmit: 0,
    statusRejected: 0,
    statusUnderReview: 0,
    reviewDaysTotal: 0,
    reviewedCount: 0,
    correspondenceIncoming: 0,
    correspondenceOutgoing: 0,
    clientPendingDays: 0,
    algihazPendingDays: 0,
    supplierPendingDays: 0,
  });

  /** An AI draft applied to the form, until the entry is saved or the draft discarded. */
  // Offered only to those who hold them somewhere; the server still decides (pass 9).
  const canEdit = useCan('qaqc', 'create_edit');
  const canSubmit = useCan('qaqc', 'submit');
  const canApprove = useCan('qaqc', 'approve_reject');

  const [aiDraft, setAiDraft] = useState<{ suggestionId: string; answers: Record<string, string> } | null>(null);

  const applyField = (field: string, value: string) => {
    if (field === 'projectId') setProjectId(value);
    else if (field === 'logDate') setDraft((current) => ({ ...current, logDate: value }));
    else if ((PROMPT_COUNTS as readonly string[]).includes(field)) {
      setDraft((current) => ({ ...current, [field]: Number(value) || 0 }));
    }
  };

  const entries = useQuery({
    queryKey: ['dg-entries'],
    queryFn: fetchDocumentGovernance,
  });

  const rollUp = useQuery({
    queryKey: ['dg-fortnight', window],
    queryFn: () => fetchFortnightRollUp(window.from, window.to),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['dg-entries'] });
    void queryClient.invalidateQueries({ queryKey: ['dg-fortnight'] });
  };

  const documentTotal = draft.submittalsTotal + draft.drawingsTotal;
  const statusTotal =
    draft.statusApproved + draft.statusResubmit + draft.statusRejected + draft.statusUnderReview;
  const reconciles = statusTotal === documentTotal;

  const run = (work: () => Promise<unknown>) => async () => {
    setError(null);
    setBlockers([]);
    try {
      await work();
      refresh();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        const details = caught.details as { blockers?: { message: string }[] } | undefined;
        if (details?.blockers) setBlockers(details.blockers.map((blocker) => blocker.message));
      } else {
        setError(t("Something went wrong."));
      }
    }
  };

  return (
    <div className="min-h-full">
      <AppTopBar app="qaqc" />
      <Breadcrumbs app="qaqc" trail={['Document Governance Log']} />

      <main className="mx-auto max-w-7xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Daily Document Governance Log")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Entered and approved daily. Distribution is fortnightly — the summary at the bottom is that view, not a second approval.")}
        </p>

        {error && (
          <div role="alert" className="mt-4 rounded-sm bg-tone-critical-surface px-4 py-3 text-sm text-tone-critical">
            <p className="m-0 font-semibold">{error}</p>
            {blockers.length > 0 && (
              <ul className="mb-0 mt-2 ps-5">
                {blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* --- Daily entry ------------------------------------------------ */}
        {canEdit && (
        <section className="mt-6 rounded-md border border-border bg-surface p-5 shadow-card">
          <h2 className="m-0 text-base font-semibold">{t("Today’s entry")}</h2>

          <FormPromptPanel
            id="dg-prompt"
            heading={t("Describe the day in a sentence")}
            label={t("Describe the day's document activity")}
            placeholder={t("e.g. Today on Project Alpha: 12 submittals, 3 drawings, 10 approved, 2 rejected, 3 under review.")}
            request={requestLogEntryDraft}
            onApply={(result) => {
              for (const [field, value] of Object.entries(result.fields)) applyField(field, value);
              setAiDraft({ suggestionId: result.suggestionId!, answers: {} });
            }}
            onAnswer={(field, value) => {
              applyField(field, value);
              setAiDraft((current) => (current ? { ...current, answers: { ...current.answers, [field]: value } } : current));
            }}
            onDiscard={() => {
              if (aiDraft) void disposeFormDraft('qaqc', aiDraft.suggestionId, { claimed: 'rejected' }).catch(() => undefined);
              setAiDraft(null);
            }}
          />

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="min-w-72 flex-1">
              <label htmlFor="dg-project" className="text-xs font-medium text-content-secondary">
                {t("Project")}
              </label>
              <ProjectPicker
                id="dg-project"
                value={projectId}
                onChange={setProjectId}
                className="mt-1 w-full rounded-sm border border-border-strong px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label htmlFor="dg-date" className="text-xs font-medium text-content-secondary">
                {t("Log date")}
              </label>
              <input
                id="dg-date"
                type="date"
                value={draft.logDate}
                onChange={(event) => setDraft({ ...draft, logDate: event.target.value })}
                className="mt-1 rounded-sm border border-border-strong px-2 py-1.5 text-sm"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {COUNT_FIELDS.map((field) => (
              <div key={field.key}>
                <label htmlFor={`dg-${field.key}`} className="text-xs font-medium text-content-secondary">
                  {t(field.label)}
                </label>
                <input
                  id={`dg-${field.key}`}
                  type="number"
                  min={0}
                  value={draft[field.key]}
                  onChange={(event) =>
                    setDraft({ ...draft, [field.key]: Number(event.target.value) || 0 })
                  }
                  className="mt-1 w-full rounded-sm border border-border-strong px-2 py-1.5 text-sm"
                />
              </div>
            ))}
          </div>

          <fieldset className="mt-4 border-0 p-0">
            <legend className="text-xs font-medium text-content-secondary">
              {t("Pending days by responsible entity")}
            </legend>
            <div className="mt-1 grid grid-cols-3 gap-3">
              {(['client', 'algihaz', 'supplier'] as const).map((entity) => {
                const key = `${entity}PendingDays` as const;
                return (
                  <div key={entity}>
                    <label htmlFor={`dg-${key}`} className="text-xs capitalize text-content-secondary">
                      {entity}
                    </label>
                    <input
                      id={`dg-${key}`}
                      type="number"
                      min={0}
                      value={draft[key]}
                      onChange={(event) =>
                        setDraft({ ...draft, [key]: Number(event.target.value) || 0 })
                      }
                      className="mt-1 w-full rounded-sm border border-border-strong px-2 py-1.5 text-sm"
                    />
                  </div>
                );
              })}
            </div>
          </fieldset>

          {/* Shown live so the problem is obvious while typing, but it only
              BLOCKS at submit — a half-entered draft must stay savable. */}
          <p
            role="status"
            className={`mt-4 rounded-sm px-3 py-2 text-xs ${
              reconciles
                ? 'bg-tone-positive-surface text-tone-positive-text'
                : 'bg-tone-pending-surface text-content-secondary'
            }`}
          >
            {reconciles
              ? t("Status breakdown reconciles to the {0} documents recorded.", { 0: documentTotal })
              : t("Status breakdown totals {0} against {1} documents. This must match before the entry can be submitted.", { 0: statusTotal, 1: documentTotal })}
          </p>

          <button
            type="button"
            disabled={!projectId}
            onClick={run(async () => {
              const saved = await saveDocumentGovernance({
                projectId,
                disciplineId: null,
                logDate: draft.logDate,
                submittalsTotal: draft.submittalsTotal,
                drawingsTotal: draft.drawingsTotal,
                statusApproved: draft.statusApproved,
                statusResubmit: draft.statusResubmit,
                statusRejected: draft.statusRejected,
                statusUnderReview: draft.statusUnderReview,
                reviewDaysTotal: draft.reviewDaysTotal,
                reviewedCount: draft.reviewedCount,
                pendingDaysByEntity: {
                  client: draft.clientPendingDays,
                  algihaz: draft.algihazPendingDays,
                  supplier: draft.supplierPendingDays,
                },
                correspondenceIncoming: draft.correspondenceIncoming,
                correspondenceOutgoing: draft.correspondenceOutgoing,
              });
              // The server decides accepted or edited from the saved entry; a
              // failure here must not undo a save that succeeded.
              if (aiDraft) {
                void disposeFormDraft('qaqc', aiDraft.suggestionId, {
                  claimed: 'accepted',
                  recordId: saved.entry.id,
                  answers: aiDraft.answers,
                }).catch(() => undefined);
                setAiDraft(null);
              }
            })}
            className="mt-4 rounded-sm bg-ag-purple px-4 py-2 text-sm font-medium text-content-inverse disabled:opacity-60"
          >
            {t("Save entry")}
          </button>
        </section>
        )}

        {/* --- Recent entries --------------------------------------------- */}
        <section className="mt-6">
          <h2 className="m-0 text-base font-semibold">{t("Recent entries")}</h2>

          {entries.isLoading && <p className="text-content-secondary">{t("Loading…")}</p>}
          {entries.data && entries.data.entries.length === 0 && (
            <p className="mt-3 rounded-md border border-border bg-tone-info-surface p-4 text-sm text-content-secondary">
              {t("No log entries yet for the projects you can see.")}
            </p>
          )}

          <ul className="mt-3 grid list-none grid-cols-1 gap-4 p-0 lg:grid-cols-2">
            {entries.data?.entries.map((entry) => (
              <li key={entry.id} className="rounded-md border border-border bg-surface p-5 shadow-card">
                <div className="flex items-start justify-between gap-3">
                  <span className="text-base font-semibold">{entry.logDate}</span>
                  <StatusChip status={entry.status} />
                </div>
                <p className="m-0 mt-1 text-sm text-content-secondary">{label(entry.projectId)}</p>

                <dl className="mt-4 flex flex-wrap gap-8">
                  <DerivedFigure
                    label={t("Average review days")}
                    value={entry.averageReviewDays}
                    explanation={entry.averageReviewDaysExplanation}
                  />
                  <DerivedFigure
                    label={t("Documents")}
                    value={entry.submittalsTotal + entry.drawingsTotal}
                  />
                </dl>

                {entry.sentBackRemarks && entry.status === 'sent_back' && (
                  <p className="mt-3 rounded-sm bg-tone-pending-surface px-3 py-2 text-xs">
                    <strong className="font-semibold">{t("Sent back:")}</strong> {entry.sentBackRemarks}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {(entry.status === 'draft' || entry.status === 'sent_back') && canSubmit && (
                    <button
                      type="button"
                      onClick={run(() => submitDocumentGovernance(entry.id))}
                      className="rounded-sm border border-ag-purple px-3 py-1.5 text-sm font-medium text-ag-purple"
                    >
                      {t("Submit")}
                    </button>
                  )}
                  {entry.status === 'submitted' && canApprove && (
                    <button
                      type="button"
                      onClick={run(() => approveDocumentGovernance(entry.id))}
                      className="rounded-sm bg-ag-purple px-3 py-1.5 text-sm font-medium text-content-inverse"
                    >
                      {t("Approve")}
                    </button>
                  )}
                </div>
                {entry.status === 'submitted' && canApprove && (
                  // Send back with remarks (FN-5): the API supported it; the
                  // screen offered only Approve.
                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <label className="min-w-60 flex-1 text-xs font-medium text-content-secondary">
                      {t("What needs changing")}
                      <input
                        value={remarks[entry.id] ?? ''}
                        onChange={(event) => setRemarks({ ...remarks, [entry.id]: event.target.value })}
                        className="mt-1 w-full rounded-sm border border-border-strong px-2 py-1.5 text-sm"
                      />
                    </label>
                    <button
                      type="button"
                      disabled={!(remarks[entry.id] ?? '').trim()}
                      onClick={run(() => sendBackDocumentGovernance(entry.id, (remarks[entry.id] ?? '').trim()))}
                      className="rounded-sm border border-border-strong px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                    >
                      {t("Send back")}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>

        {/* --- Fortnightly roll-up ---------------------------------------- */}
        <section className="mt-8 rounded-md border border-border bg-surface p-5 shadow-card">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="m-0 text-base font-semibold">{t("Fortnightly summary")}</h2>
              <p className="mt-1 text-sm text-content-secondary">
                {t("The distribution view. Aggregated across the window, not approved as a unit.")}
              </p>
            </div>
            <div className="flex gap-3">
              <div>
                <label htmlFor="rollup-from" className="text-xs font-medium text-content-secondary">
                  {t("From")}
                </label>
                <input
                  id="rollup-from"
                  type="date"
                  value={window.from}
                  onChange={(event) => setWindow({ ...window, from: event.target.value })}
                  className="mt-1 rounded-sm border border-border-strong px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label htmlFor="rollup-to" className="text-xs font-medium text-content-secondary">
                  {t("To")}
                </label>
                <input
                  id="rollup-to"
                  type="date"
                  value={window.to}
                  onChange={(event) => setWindow({ ...window, to: event.target.value })}
                  className="mt-1 rounded-sm border border-border-strong px-2 py-1.5 text-sm"
                />
              </div>
            </div>
          </div>

          {rollUp.data && (
            <>
              <dl className="mt-5 grid grid-cols-2 gap-6 sm:grid-cols-4">
                <DerivedFigure label={t("Entries")} value={rollUp.data.entryCount} />
                <DerivedFigure label={t("Approved")} value={rollUp.data.approvedEntryCount} />
                <DerivedFigure
                  label={t("Average review days")}
                  value={rollUp.data.averageReviewDays}
                  explanation={rollUp.data.averageReviewDaysExplanation}
                />
                <DerivedFigure
                  label={t("Documents")}
                  value={
                    (rollUp.data.totals.submittals ?? 0) + (rollUp.data.totals.drawings ?? 0)
                  }
                />
              </dl>

              {Object.keys(rollUp.data.pendingDaysByEntity).length > 0 && (
                <div className="mt-5 border-t border-border pt-4">
                  <h3 className="m-0 text-sm font-semibold">{t("Pending days by responsible entity")}</h3>
                  <dl className="mt-3 grid grid-cols-3 gap-6">
                    {Object.entries(rollUp.data.pendingDaysByEntity).map(([entity, days]) => (
                      <DerivedFigure
                        key={entity}
                        label={entity.charAt(0).toUpperCase() + entity.slice(1)}
                        value={days}
                      />
                    ))}
                  </dl>
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
